// permissions/PermissionsContext.tsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import * as Location from 'expo-location'
import * as Notifications from 'expo-notifications'
import { Linking, Platform, Text, View, AppState } from 'react-native'
import { CustomActionSheet, CustomActionSheetRef } from '../CustomActionSheet'
import { ThemedButton } from '../../utility/input/buttons'
import { textStyles } from '../../style'
import { ThemeContext } from '../../utility/context/ThemeContext'
import { useAppDispatch, useAppSelector } from '../../store'
import { DevicePermissions, NotificationChannels, NotificationPermissionRecord, PermissionRecord } from '@ecountabl/lib'
import { setAppPermissions, setAppPermission } from '../../auth-slice'
import api from '../../api-client'
import { extra } from '../../config'
import { log } from '../../utility/pwa'
import ThemedDialog from '../../style/dialog'
import { Ionicons } from '@expo/vector-icons'
import Analytics from '../analytics'
import * as ExpoLinking from 'expo-linking'
import * as ExpoApplication from 'expo-application'

type PermissionType = 'location' | 'notifications'

interface PermissionsContextValue {
  getLocation: () => Promise<Location.LocationObject>
  getNotificationToken: () => Promise<string | undefined>
  enablePermission: (type: PermissionType) => void
  disablePermission: (type: PermissionType) => void
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined)

const permissionAPIs = {
  location: {
    get: Location.getForegroundPermissionsAsync,
    // request: Location.requestForegroundPermissionsAsync,
    request: async () => ({ granted: false, canAskAgain: false } as any),
  },
  notifications: {
    get: Notifications.getPermissionsAsync,
    request: Notifications.requestPermissionsAsync,
  },
}

type Props = {
  children: React.ReactNode
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = (props: Props) => {
  const theme = useContext(ThemeContext).theme
  const { textMain, danger, warning, primary } = theme.colors

  const { user, deviceId } = useAppSelector(state => state.auth)
  const dispatch = useAppDispatch()

  const [settingsSheet, setSettingsSheet] = useState<null | PermissionType>(null)
  const sheetRef = React.useRef<CustomActionSheetRef>(null)

  const [showErrorDialog, setShowErrorDialog] = useState(false)

  const savePermission = useCallback((
    type: PermissionType, permission: PermissionRecord | NotificationPermissionRecord
  ) => {
    const oldPermission = user.appPermissions[deviceId][type]

    dispatch(setAppPermission({type, permission}))

    api.updateAppPermissions(deviceId, {[type]: permission}).catch((err) => {
      log.error(err, { tags: { message: 'error updating app permissions' } })
      dispatch(setAppPermission({type, permission: oldPermission}))
    })
  }, [user, deviceId, dispatch])

  const saveDevicePermissions = useCallback((permissions: DevicePermissions) => {
    dispatch(setAppPermissions(permissions))

    api.updateAppPermissions(deviceId, permissions).catch((err) => {
      log.error(err, { tags: { message: 'error updating app permissions' } })
    })
  }, [deviceId, dispatch])

  const renderActionSheetContent = (type: PermissionType) => {
    if (!type) {
      return null
    }

    const texts = [
      `1. Open your app settings`,
      `2. Find ${settingsSheet} permissions`,
      `3. Enable ${settingsSheet} permissions`,
    ]

    return (
      <View style={{gap: 10, paddingHorizontal: 16}}>
        <Text style={[textStyles.h2, {color: textMain}]}>
          Enable Permissions in Your Device Settings
        </Text>
        <Text style={[textStyles.body, {color: textMain}]}>
          {`Uh oh, looks like you need to enable ${settingsSheet} permissions from your device settings.`}
        </Text>
        <View style={{gap: 5}}>
        {
          texts.map((text, index) => (
            <Text key={index} style={[textStyles.body, {color: textMain}]}>
              {text}
            </Text>
          ))
        }
        </View>
        <Text style={[textStyles.body, {color: textMain}]}>
          {`When you're done, come back to the app and enable the ${settingsSheet} permission. This may require you to close the app and reopen it.`}
        </Text>
        <View style={{paddingHorizontal: 16, paddingTop: 16}}>
          <ThemedButton
            text='Open App Settings'
            onPress={async () => {
              Linking.openURL('app-settings:')
            }}
            variant="primary"
          />
        </View>
        
      </View>
    )
  }

  const getLocation = useCallback(async () => {
    const { status } = await Location.getForegroundPermissionsAsync()
    if (status !== 'granted') {
      throw new Error('Location permission not granted')
    }
    return await Location.getCurrentPositionAsync()
  }, [])

  const getNotificationToken = useCallback(async () => {
    if (Platform.OS === 'web') {
      return undefined
    }

    const { status } = await Notifications.getPermissionsAsync()
    if (status !== 'granted') {
      throw new Error('Notification permission not granted')
    }

    let token = undefined
    try {
      token = await Notifications.getExpoPushTokenAsync({
        projectId: extra.eas.projectId
      })
    } catch (error) {
      log.error(error, { tags: { message: 'error getting expo push token' } })
    }
    return token?.data
  }, [])

  const enablePermission = async (type: PermissionType) => {
    if (type === 'notifications' && Platform.OS === 'web') {
      return
    }
    const { get, request } = permissionAPIs[type]

    const { granted, canAskAgain } = await get()
    if (!granted) {
      if (canAskAgain) {
        try {
          const { granted: grantedAfterRequest } = await request()
          if (grantedAfterRequest) {
            const pushToken = type === 'notifications' ? await getNotificationToken() : undefined

            const newPermission = {
              status: 'granted',
              pushToken
            }

            savePermission(type, newPermission as NotificationPermissionRecord | PermissionRecord)
          }
        } catch (err) {
          setSettingsSheet(type)
        }
      } else {
        setSettingsSheet(type)
      }
    } else {
      const pushToken = type === 'notifications' ? await getNotificationToken() : undefined

      const newPermission= {
        status: 'granted',
        pushToken,
      }
      
      savePermission(type, newPermission as NotificationPermissionRecord | PermissionRecord)
    }
  }

  const disablePermission = async (type: PermissionType) => {
    const newPermission = {
      status: 'denied'
    }

    savePermission(type, newPermission as NotificationPermissionRecord | PermissionRecord)
  }

  const syncUserAppPermissions = useCallback(async () => {
    if (!user || Object.keys(user).length === 0 || !deviceId) return
    if (Platform.OS === 'web') return

    const userPermissions = (user.appPermissions || {})[deviceId]

    const { status: notificiationStatus } = await Notifications.getPermissionsAsync()
    const { status: locationStatus } = await Location.getForegroundPermissionsAsync()

    if (notificiationStatus === 'granted') {
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
            shouldShowAlert: true,
            shouldPlaySound: true,
            shouldSetBadge: true,
        })
      })

      // if android, set up notification channels
      if (Platform.OS === 'android') {
        try {
          await Notifications.setNotificationChannelAsync(NotificationChannels.DEFAULT, {
            name: 'default',
            importance: Notifications.AndroidImportance.DEFAULT,
            vibrationPattern: [0, 200],
            lightColor: primary,
            showBadge: true
          })

          await Notifications.setNotificationChannelAsync(NotificationChannels.WARNING, {
            name: 'warning',
            importance: Notifications.AndroidImportance.HIGH,
            vibrationPattern: [0, 200, 100, 300],
            lightColor: warning,
            showBadge: true,
          })

          await Notifications.setNotificationChannelAsync(NotificationChannels.URGENT, {
            name: 'urgent',
            importance: Notifications.AndroidImportance.MAX,
            vibrationPattern: [0, 300, 100, 400, 100, 500],
            lightColor: danger,
            showBadge: true
          })
        } catch (err) {
          log.error(err, { tags: { message: 'error setting up notification channels' } })
        }
      }
    }

    // create a new permissions record if missing
    if (!userPermissions) {
      const pushToken = notificiationStatus === 'granted' ? await getNotificationToken() : undefined

      const newPermission: DevicePermissions = {
        location: {
          status: locationStatus
        },
        notifications: {
          status: notificiationStatus,
          pushToken
        }
      }

      saveDevicePermissions(newPermission)
      return
    }

    // reconcile with conservative rules:
    // - Only DOWNGRADE to denied if device denies
    // - Do NOT auto-UPGRADE to granted if user has explicitly denied
    // - Optionally upgrade only from undetermined -> granted (first sync)
    const currentLocation = userPermissions.location?.status || 'undetermined'
    const currentNotifications = userPermissions.notifications?.status || 'undetermined'

    const shouldDowngradeLocation = locationStatus === 'denied' && currentLocation !== 'denied'
    const shouldDowngradeNotifications = notificiationStatus === 'denied' && currentNotifications !== 'denied'

    const shouldUpgradeLocation = locationStatus === 'granted' && currentLocation === 'undetermined'
    const shouldUpgradeNotifications = notificiationStatus === 'granted' && currentNotifications === 'undetermined'

    if (shouldDowngradeLocation && shouldDowngradeNotifications) {
      saveDevicePermissions({
        location: { status: 'denied' },
        notifications: { status: 'denied' }
      })
    } else if (shouldDowngradeLocation) {
      savePermission('location', { status: 'denied' })
    } else if (shouldDowngradeNotifications) {
      savePermission('notifications', { status: 'denied' })
    }

    if (shouldUpgradeLocation && shouldUpgradeNotifications) {
      const pushToken = await getNotificationToken()
      saveDevicePermissions({
        location: { status: 'granted' },
        notifications: { status: 'granted', pushToken }
      })
    } else if (shouldUpgradeLocation) {
      savePermission('location', { status: 'granted' })
    } else if (shouldUpgradeNotifications) {
      const pushToken = await getNotificationToken()
      savePermission('notifications', { status: 'granted', pushToken } as NotificationPermissionRecord)
    }

    // if notifications are marked granted in app AND device is granted
    // but token is missing, refresh it
    if (currentNotifications === 'granted' && notificiationStatus === 'granted' && !userPermissions.notifications?.pushToken) {
      const pushToken = await getNotificationToken()
      if (pushToken) {
        savePermission('notifications', { status: 'granted', pushToken } as NotificationPermissionRecord)
      }
    }
  }, [user, deviceId, getNotificationToken, saveDevicePermissions, savePermission])


  const clearNotificationBadge = useCallback(() => {
    if (Platform.OS !== 'web') {
      try {
        Notifications.setBadgeCountAsync(0)
      } catch (e) {
        log.error(e, { tags: { message: 'error clearing notification badge' } })
      }
    }
  }, [])

  // sync user appPermissions
  useEffect(() => {
    if (!user || Object.keys(user).length === 0 || !deviceId) return
    syncUserAppPermissions()
  }, [user, user?.appPermissions, deviceId, syncUserAppPermissions])

  // re-check and sync when app comes to foreground
  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        clearNotificationBadge()
        syncUserAppPermissions()
      }
    })
    return () => sub.remove()
  }, [syncUserAppPermissions])

  // track when app is opened via notification press (cold start + in-app)
  useEffect(() => {
    if (Platform.OS === 'web') return
    
    const handleResponse = (response: Notifications.NotificationResponse | null) => {
      if (!response) return
      try {
        const { notification, actionIdentifier, userText } = response
        const data = notification?.request?.content?.data as Record<string, unknown> | undefined
        Analytics.track('notification_opened', {
          actionIdentifier,
          userText,
          data,
          title: notification?.request?.content?.title,
          body: notification?.request?.content?.body,
        })

        // if the notification has a destination path, alert the user and open the app to that path
        if (data?.destinationPath) {
          const url = ExpoLinking.createURL(
            data.destinationPath as string,
            {
              scheme: ExpoApplication.applicationId
            }
          )
          Linking.openURL(url)
        }
      } catch (e) {
        log.error(e, { tags: { message: 'error tracking notification open' } })
      }
    }

    const checkInitial = async () => {
      try {
        const last = await Notifications.getLastNotificationResponseAsync()
        if (last) {
          handleResponse(last)
        }
      } catch (e) {
        log.error(e, { tags: { message: 'error getting last notification response' } })
      }
    }

    checkInitial()

    clearNotificationBadge()

    const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
      handleResponse(response)
    })

    return () => {
      subscription.remove()
    }
  }, [])

  useEffect(() => {
    if (settingsSheet) {
      sheetRef.current?.show()
    } else {
      sheetRef.current?.hide()
    }
  }, [settingsSheet])

  return (
    <PermissionsContext.Provider
      value={{
        getLocation,
        getNotificationToken,
        enablePermission,
        disablePermission
      }}
    >
      {props.children}
      <CustomActionSheet
        ref={sheetRef}
        noInput={true}
        onDismiss={() => setSettingsSheet(null)}
        name="permissions-settings"
      >
        {renderActionSheetContent(settingsSheet)}
      </CustomActionSheet>
      <ThemedDialog
        errorAnimation={true}
        visible={showErrorDialog}
        title='Error'
        subMessage='There was an error saving your permission preferences'
        options={[
          {
            title: 'OK',
            icon: <Ionicons name='checkmark' size={24} />,
            role: 'destructive',
            onPress: () => {
              setShowErrorDialog(false)
            }
          }
        ]}
      />
    </PermissionsContext.Provider>
  )
}

export const usePermissions = (): PermissionsContextValue => {
  const ctx = useContext(PermissionsContext)
  
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return ctx
}

